import React from 'react'

import { CellContext, RowData } from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'

import { MessageIcon } from '../../atoms/icons/MessageIcon'
import { colors } from '../../styles/colors'

const renderCustomFieldValue = (props: CellContext<RowData, string>) => {
  const cellValue = props.getValue()
  if (typeof cellValue === 'number') {
    return <div>{cellValue}</div>
  } else if (typeof cellValue === 'string') {
    return <div>{<MessageIcon fillColor={colors.charcoal6} size={20} />}</div>
  } else return <div></div>
}

export const ProviderUIMetadataComponent = {
  // Check Ins such coachingCheckIn, bctCheckIn, bccCheckIn, bcmCheckIn
  checkIn: {
    report: {
      customColumns: [
        {
          Header: 'Notes',
          accessor: 'response.notes',
          name: 'notes',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue() && <MessageIcon fillColor={colors.charcoal6} size={20} />}</div>
          },
        },
        {
          accessor: 'response.checkIn0',
          name: 'checkIn0',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn1',
          name: 'checkIn1',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn2',
          name: 'checkIn2',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn3',
          name: 'checkIn3',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn4',
          name: 'checkIn4',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn5',
          name: 'checkIn5',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn6',
          name: 'checkIn6',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn7',
          name: 'checkIn7',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn8',
          name: 'checkIn8',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
        {
          accessor: 'response.checkIn9',
          name: 'checkIn9',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return renderCustomFieldValue(props)
          },
        },
      ],
      fixedColumns: [
        {
          Header: 'Date',
          accessor: 'submit_date',
          id: 'submitDate',
          name: 'date',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return <span>{format(parseISO(props.getValue()), 'MMM d')}</span>
          },
        },
      ],
    },
  },
  baseSleepDiary: {
    report: {
      customColumns: [
        {
          Header: 'Meds/substances',
          accessor: 'response.describeMedications',
          name: 'describeMedications',
          width: 150,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Time in bed',
          accessor: 'response.timeWhenInBed',
          name: 'timeWhenInBed',
          width: 100,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Time asleep',
          accessor: 'response.timeWhenFallAsleep',
          name: 'timeWhenFallAsleep',
          width: 100,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: '# Awakenings',
          accessor: 'response.wakeUpCount',
          name: 'wakeUpCount',
          width: 120,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Time awake',
          accessor: 'response.timeOfWakeUp',
          name: 'timeOfWakeUp',
          width: 100,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Time up',
          accessor: 'response.timeWhenOutOfBed',
          name: 'timeWhenOutOfBed',
          width: 100,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Sleep quality',
          accessor: 'response.qualityOfSleep',
          name: 'qualityOfSleep',
          width: 120,
          Cell: (props: CellContext<RowData, number>) => {
            const responseValues = ['Very poor', 'Poor', 'Fair', 'Good', 'Very good']
            return <div>{responseValues[props.getValue()]}</div>
          },
        },
        {
          Header: 'Caffeine',
          accessor: 'response.caffeine',
          name: 'caffeine',
          width: 100,
          Cell: (props: CellContext<RowData, number>) => {
            const responseValues = ['Yes', 'No']
            return <div>{responseValues[props.getValue()]}</div>
          },
        },
        {
          Header: '# Naps',
          accessor: 'response.napOrDozeAmount',
          name: 'napOrDozeAmount',
          width: 80,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Nap length',
          accessor: 'response.napOrDozeTime',
          name: 'napOrDozeTime',
          width: 100,
          Cell: (props: CellContext<RowData, string>) => {
            return <div>{props.getValue()}</div>
          },
        },
        {
          Header: 'Sleepiness',
          accessor: 'response.howSleepy',
          name: 'howSleepy',
          width: 150,
          Cell: (props: CellContext<RowData, number>) => {
            const responseValues = ['So sleepy', 'Somewhat tired', 'Fairly tired', 'Alert']
            return <div>{responseValues[props.getValue()]}</div>
          },
        },
      ],
      fixedColumns: [
        {
          Header: 'Date',
          accessor: 'submit_date',
          id: 'submitDate',
          name: 'date',
          width: 70,
          Cell: (props: CellContext<RowData, string>) => {
            return <span>{format(parseISO(props.getValue()), 'MMM d')}</span>
          },
        },
      ],
    },
  },
  baseTherapyTargets: {
    customFields: {
      fields: {
        sliderField: {
          minValue: 0,
          maxValue: 10,
          minLabel: '0: Not a problem',
          maxLabel: '10: A very big problem',
          step: 1,
        },
      },
    },
  },
  baseExposureHierarchy: {
    customFields: {
      fields: {
        sliderField: {
          minValue: 0,
          maxValue: 100,
          minLabel: 'No Distress',
          maxLabel: 'Extreme Distress',
          step: 5,
        },
      },
    },
  },
  baseYouthTopProblems: {
    customFields: {
      fields: [
        { type: 'select', placeholder: 'Select rank', enums: [1, 2, 3], enumNames: ['1', '2', '3'] },
        {
          type: 'range',
          minValue: 0,
          maxValue: 10,
          minLabel: 'Not a problem',
          maxLabel: 'Very big problem',
          noTitle: true,
          step: 1,
        },
      ],
      maxNumberOfFieldGroups: 3,
    },
  },
  bccTopProblems: {
    customFields: {
      fields: [
        { type: 'select', placeholder: 'Select rank', enums: [1, 2, 3], enumNames: ['1', '2', '3'] },
        {
          type: 'range',
          minValue: 0,
          maxValue: 10,
          minLabel: 'Not a problem',
          maxLabel: 'Very big problem',
          noTitle: true,
          step: 1,
        },
      ],
      maxNumberOfFieldGroups: 3,
    },
  },
}
